<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, shallowRef, watch } from 'vue'

import { ApiError, publicApi } from '../api'
import { useClicker } from '../clicker'
import LoadingState from '../components/LoadingState.vue'
import QrCode from '../components/QrCode.vue'
import { preloadSlides } from '../media'
import { bootstrapSession, useSession } from '../session'
import type { PublicPresentation } from '../types'

const props = defineProps<{
  slug: string
}>()

const deck = ref<PublicPresentation | null>(null)
const session = useSession()
const localIndex = ref(0)
const error = ref('')
const loading = ref(true)
const roomCode = ref('')
const controllerUrl = ref('')
const controlsVisible = ref(true)
const clickerUnavailable = ref(false)
const clicker = shallowRef<ReturnType<typeof useClicker> | null>(null)
let hideTimer = 0
let roomRefreshTimer = 0
let touchStartX = 0
let touchStartY = 0
let syncingRevision = 0

const count = computed(() => deck.value?.doc.slides.length ?? 0)
const current = computed(() => {
  const value = clicker.value?.state.value.slideIndex ?? localIndex.value
  return Math.max(0, Math.min(Math.max(0, count.value - 1), value))
})
const connectionStatus = computed(() => clicker.value?.status.value ?? 'offline')
const controllerConnected = computed(() => clicker.value?.controllerConnected.value ?? false)
const absoluteControllerUrl = computed(() => {
  if (!controllerUrl.value) return ''
  return new URL(controllerUrl.value, window.location.origin).toString()
})

async function load(): Promise<void> {
  try {
    deck.value = await publicApi.get(props.slug)
    await preloadSlides(deck.value.assets.slides)
    document.title = `${deck.value.title} — показ`
    localIndex.value = 0
    await bootstrapSession()
    try {
      if (session.state.authenticated) {
        await refreshRoom()
      } else {
        clickerUnavailable.value = true
      }
    } catch (cause) {
      if (cause instanceof ApiError && [401, 403].includes(cause.status)) {
        clickerUnavailable.value = true
      } else {
        console.warn('Clicker is unavailable', cause)
        scheduleRoomRetry()
      }
    }
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : 'Не удалось открыть показ.'
  } finally {
    loading.value = false
  }
}

async function syncDeckRevision(revision: number): Promise<void> {
  if (!deck.value || deck.value.revision === revision || syncingRevision === revision) return
  syncingRevision = revision
  try {
    const fresh = await publicApi.get(props.slug)
    await preloadSlides(fresh.assets.slides)
    deck.value = fresh
    localIndex.value = Math.min(localIndex.value, fresh.doc.slides.length - 1)
    document.title = `${fresh.title} — показ`
  } catch (cause) {
    console.warn('Could not refresh published deck', cause)
  } finally {
    syncingRevision = 0
  }
}

async function refreshRoom(): Promise<void> {
  const room = await publicApi.createClicker(props.slug)
  roomCode.value = room.roomCode
  controllerUrl.value = room.controllerUrl
  localIndex.value = room.slideIndex
  clicker.value?.close()
  clicker.value = useClicker({
    role: 'screen',
    slug: props.slug,
    token: room.screenToken,
    wsPath: room.wsPath,
    initial: {
      revision: room.revision,
      slideIndex: room.slideIndex,
      seq: room.seq,
    },
  })
  const expiresAt = new Date(room.controllerExpiresAt).getTime()
  const delay = Number.isFinite(expiresAt)
    ? Math.max(30_000, expiresAt - Date.now() - 30_000)
    : 9 * 60 * 1_000
  window.clearTimeout(roomRefreshTimer)
  roomRefreshTimer = window.setTimeout(() => {
    void refreshRoom().catch((cause) => {
      console.warn('Could not refresh clicker QR', cause)
      scheduleRoomRetry()
    })
  }, delay)
}

function scheduleRoomRetry(): void {
  window.clearTimeout(roomRefreshTimer)
  roomRefreshTimer = window.setTimeout(() => {
    void refreshRoom().catch((cause) => {
      console.warn('Could not start clicker', cause)
      scheduleRoomRetry()
    })
  }, 30_000)
}

function go(to: number): void {
  if (!count.value) return
  const target = Math.max(0, Math.min(count.value - 1, to))
  localIndex.value = target
  clicker.value?.navigate(target)
}

function onKeydown(event: KeyboardEvent): void {
  if (['ArrowRight', 'ArrowDown', 'PageDown', ' ', 'Enter'].includes(event.key)) {
    event.preventDefault()
    go(current.value + 1)
  } else if (['ArrowLeft', 'ArrowUp', 'PageUp', 'Backspace'].includes(event.key)) {
    event.preventDefault()
    go(current.value - 1)
  } else if (event.key === 'Home') {
    event.preventDefault()
    go(0)
  } else if (event.key === 'End') {
    event.preventDefault()
    go(count.value - 1)
  } else if (event.key.toLowerCase() === 'f') {
    event.preventDefault()
    void toggleFullscreen()
  } else if (event.key === 'Escape') {
    window.location.href = `/p/${encodeURIComponent(props.slug)}`
  }
}

function onTouchStart(event: TouchEvent): void {
  touchStartX = event.touches[0]?.clientX ?? 0
  touchStartY = event.touches[0]?.clientY ?? 0
}

function onTouchEnd(event: TouchEvent): void {
  const touch = event.changedTouches[0]
  if (!touch) return
  const dx = touch.clientX - touchStartX
  const dy = touch.clientY - touchStartY
  if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy)) {
    go(current.value + (dx < 0 ? 1 : -1))
  }
}

async function toggleFullscreen(): Promise<void> {
  if (document.fullscreenElement) await document.exitFullscreen()
  else await document.documentElement.requestFullscreen()
}

function showControls(): void {
  controlsVisible.value = true
  window.clearTimeout(hideTimer)
  hideTimer = window.setTimeout(() => {
    controlsVisible.value = false
  }, 2_400)
}

onMounted(() => {
  void load()
  window.addEventListener('keydown', onKeydown)
  window.addEventListener('mousemove', showControls)
  showControls()
})

watch(
  () => clicker.value?.state.value.revision ?? 0,
  (revision) => {
    if (revision > 0) void syncDeckRevision(revision)
  },
)

onBeforeUnmount(() => {
  clicker.value?.close()
  window.clearTimeout(hideTimer)
  window.clearTimeout(roomRefreshTimer)
  window.removeEventListener('keydown', onKeydown)
  window.removeEventListener('mousemove', showControls)
})
</script>

<template>
  <main
    class="show-page"
    :class="{ 'controls-hidden': !controlsVisible }"
    @touchstart.passive="onTouchStart"
    @touchend.passive="onTouchEnd"
    @click="showControls"
  >
    <LoadingState v-if="loading" title="Готовим показ" full />
    <section v-else-if="error || !deck" class="show-error">
      <h1>Показ не открылся</h1>
      <p>{{ error || 'Презентация не найдена.' }}</p>
      <a :href="`/p/${slug}`" class="button button--secondary">Вернуться</a>
    </section>

    <template v-else>
      <img
        class="show-slide"
        :src="deck.assets.slides[current] ?? ''"
        :alt="`Слайд ${current + 1} из ${count}`"
        draggable="false"
      >

      <aside v-if="current === 0 && roomCode && absoluteControllerUrl" class="room-card">
        <QrCode :value="absoluteControllerUrl" label="Подключить телефон как пульт" />
        <div>
          <span>Телефон станет пультом</span>
          <strong>{{ roomCode }}</strong>
          <small>Камера или getslides.xkuragax.ru/remote</small>
        </div>
      </aside>

      <div class="show-topbar">
        <a :href="`/p/${slug}`" class="show-control" aria-label="Выйти из показа">×</a>
        <div class="show-connection" :class="{ 'is-online': connectionStatus === 'connected' }">
          <span></span>
          {{
            clickerUnavailable
              ? 'Пульт доступен владельцу'
              : controllerConnected
                ? 'Пульт подключён'
                : connectionStatus === 'connected'
                  ? 'Ждём пульт'
                  : 'Переподключаемся'
          }}
        </div>
        <button type="button" class="show-control" aria-label="Полный экран" @click="toggleFullscreen">
          <svg class="fullscreen-icon" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M4 9V4h5M15 4h5v5M20 15v5h-5M9 20H4v-5" />
          </svg>
        </button>
      </div>

      <button
        type="button"
        class="show-arrow show-arrow--left"
        :disabled="current === 0"
        aria-label="Предыдущий слайд"
        @click.stop="go(current - 1)"
      >←</button>
      <button
        type="button"
        class="show-arrow show-arrow--right"
        :disabled="current === count - 1"
        aria-label="Следующий слайд"
        @click.stop="go(current + 1)"
      >→</button>

      <div class="show-counter">
        <span>{{ String(current + 1).padStart(2, '0') }}</span>
        <i></i>
        <span>{{ String(count).padStart(2, '0') }}</span>
      </div>
    </template>
  </main>
</template>
